import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Shield, Loader2, Copy, Check, AlertTriangle, ScanFace, Fingerprint } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useWebAuthn } from "@/hooks/useWebAuthn";

const MFASetup = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [factorId, setFactorId] = useState("");
  const [qrUri, setQrUri] = useState("");
  const [secret, setSecret] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);

  const { isAvailable: biometricAvailable, loading: biometricLoading, register: registerBiometric, hasCredential } = useWebAuthn();

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const getBiometricLabel = () => {
    if (isIOS) return "Face ID";
    if (isAndroid) return "بصمة الإصبع / الوجه";
    return "المصادقة البيومترية";
  };
  const getBiometricIcon = () => {
    if (isIOS) return <ScanFace className="w-5 h-5" />;
    return <Fingerprint className="w-5 h-5" />;
  };

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    enrollMFA();
  }, [user]);

  const enrollMFA = async () => {
    setEnrolling(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        localStorage.removeItem("mfa_enroll_data");
        navigate("/auth");
        return;
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        localStorage.removeItem("mfa_enroll_data");
        navigate("/auth");
        return;
      }

      const userFactors = userData.user.factors || [];

      const existingUnverified = userFactors.find(
        (f) => f.factor_type === "totp" && (f.status as string) === "unverified"
      );

      // Try to restore saved enrollment data from localStorage
      const savedData = localStorage.getItem("mfa_enroll_data");
      if (savedData && existingUnverified) {
        try {
          const parsed = JSON.parse(savedData);
          if (parsed.factorId === existingUnverified.id && parsed.qrUri && parsed.secret) {
            setFactorId(parsed.factorId);
            setQrUri(parsed.qrUri);
            setSecret(parsed.secret);
            setEnrolling(false);
            return;
          }
        } catch {
          // Invalid saved data, continue with fresh enroll
        }
      }

      // Clean up any existing unverified factors
      for (const f of userFactors) {
        if (f.factor_type === "totp" && (f.status as string) === "unverified") {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `crebtopay.com (${userData.user.email})`,
        issuer: "crebtopay.com",
      });

      if (error) throw error;

      // Save enrollment data to localStorage so it persists across page reloads
      localStorage.setItem("mfa_enroll_data", JSON.stringify({
        factorId: data.id,
        qrUri: data.totp.uri,
        secret: data.totp.secret,
      }));

      setFactorId(data.id);
      setQrUri(data.totp.uri);
      setSecret(data.totp.secret);
    } catch (error: any) {
      console.error("MFA enroll error:", error);
      
      // If AAL2 is required, keep session and return user to MFA verify flow
      if (error?.code === "insufficient_aal" || error?.message?.includes("AAL2")) {
        toast({
          title: t("mfa.enrollError"),
          description: t("mfa.needRelogin"),
          variant: "destructive",
        });
        navigate("/mfa-verify");
        return;
      }
      
      toast({
        title: t("mfa.enrollError"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setEnrolling(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifyCode || verifyCode.length !== 6) return;

    setLoading(true);
    try {
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      // Clear saved enrollment data after successful verification
      localStorage.removeItem("mfa_enroll_data");

      toast({
        title: t("mfa.setupSuccess"),
        description: t("mfa.setupSuccessDesc"),
      });

      // Show biometric setup if available, otherwise go to dashboard
      if (biometricAvailable && !hasCredential) {
        setShowBiometricSetup(true);
      } else {
        navigate("/wallet");
      }
    } catch (error: any) {
      console.error("MFA verify error:", error);
      toast({
        title: t("mfa.verifyError"),
        description: t("mfa.invalidCode"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricRegister = async () => {
    const result = await registerBiometric();
    if (result.success) {
      toast({ title: "تم تسجيل البصمة بنجاح", description: "يمكنك الآن تسجيل الدخول باستخدام البصمة" });
      setShowBiometricSetup(false);
      navigate("/wallet");
    } else {
      toast({
        title: "فشل تسجيل البصمة",
        description: result.error || "حدث خطأ، حاول مرة أخرى",
        variant: "destructive",
      });
      // Stay on biometric setup - don't navigate away
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-crypto-purple/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "-3s" }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-2xl font-bold text-foreground">{t("mfa.setupTitle")}</h1>
          <p className="text-muted-foreground mt-2">{t("mfa.setupDesc")}</p>
        </div>

        <div className="glass rounded-2xl p-8 shadow-card animate-scale-in">
          {showBiometricSetup ? (
            <div className="space-y-4 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-2">
                {getBiometricIcon()}
              </div>
              <h3 className="text-lg font-semibold text-foreground">تسجيل {getBiometricLabel()}</h3>
              <p className="text-sm text-muted-foreground">
                سجّل بصمتك الآن لتسجيل الدخول بسرعة في المرات القادمة
              </p>
              <Button
                onClick={handleBiometricRegister}
                disabled={biometricLoading}
                className="w-full h-12 gradient-primary text-primary-foreground font-semibold"
              >
                {biometricLoading ? "جارٍ التسجيل..." : `تسجيل ${getBiometricLabel()}`}
              </Button>
            </div>
          ) : enrolling ? (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
              <p className="text-muted-foreground">{t("common.loading")}</p>
            </div>
          ) : (
            <>
              {/* QR Code */}
              <div className="flex flex-col items-center mb-6">
                <p className="text-sm text-muted-foreground mb-4 text-center">
                  {t("mfa.scanQR")}
                </p>
                <div className="bg-white p-4 rounded-xl mb-4 max-w-full overflow-hidden">
                  {!qrUri ? (
                    <p className="text-xs text-muted-foreground">QR unavailable</p>
                  ) : qrUri.startsWith("data:image") ? (
                    <img src={qrUri} alt="MFA QR Code" className="w-[200px] h-[200px]" />
                  ) : (
                    <QRCodeSVG value={qrUri} size={200} />
                  )}
                </div>

                {/* Manual secret */}
                <p className="text-xs text-muted-foreground mb-2">{t("mfa.orEnterManually")}</p>
                 <div className="flex items-center gap-2 bg-secondary rounded-lg px-3 py-2 w-full">
                  <code className="text-xs text-foreground flex-1 break-all font-mono" dir="ltr">
                    {secret}
                  </code>
                  <button onClick={copySecret} className="shrink-0 text-muted-foreground hover:text-primary transition-colors">
                    {copied ? <Check className="w-4 h-4 text-crypto-green" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>

                {/* Warning to save secret */}
                <div className="mt-4 flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200/90 leading-relaxed">
                    {t("mfa.saveSecretWarning")}
                  </p>
                </div>
              </div>

              {/* Verify form */}
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground">{t("mfa.enterCode")}</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                    className="bg-input border-border text-foreground text-center text-2xl tracking-[0.5em] font-mono"
                    dir="ltr"
                    autoFocus
                    autoComplete="one-time-code"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || verifyCode.length !== 6}
                  className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t("mfa.activate")
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
